import ColumnEvent from '../schema/ColumnEvent';
import ConflictGroup from '../schema/ConflictGroup';
import ConflictGroupColumns from '../schema/ConflictGroupColumns';
import NormalisedEvent from '../schema/NormalisedEvent';
import { getTimestamp } from 'owa-datetime';

/**
 * Takes a conflict group and puts its events into the minimal number of columns possible, preserving the event order in the conflict group
 * and also ensuring that no 2 events in a conflict group overlap
 * @param conflictGroup
 */
const arrangeIntoDisjointColumns = (conflictGroup: ConflictGroup): ConflictGroupColumns => {
    if (!conflictGroup || !conflictGroup.events || !conflictGroup.events.length) {
        return undefined;
    }

    const columnGroup: ConflictGroupColumns = {
        start: conflictGroup.start,
        end: conflictGroup.end,
        columns: [],
    };

    const { columns } = columnGroup;

    // Since in every iteration we will be modifying the input list of events, we clone it at first
    const remainingEvents = [...conflictGroup.events];

    while (remainingEvents.length) {
        /**
         * Make a new column with the first element in the remaining elements and then remove it and check which other events in the array
         * in the order of traversal do not strictly overlap with the column so far. These events can be appended into the column into the end
         * There might be columnGroups with single Column */

        // get the first of the remaining items
        const selectorEvent: NormalisedEvent = remainingEvents.shift();

        // initiate a candidateGroup
        const currentColumn: ColumnEvent[] = [
            { event: selectorEvent, startColumn: columns.length, endColumn: columns.length },
        ];

        let currentEventIndex: number = 0;

        while (currentEventIndex < remainingEvents.length) {
            const currentEvent: NormalisedEvent = remainingEvents[currentEventIndex];
            const currentColumnStart: number = getTimestamp(currentColumn[0].event.start);
            const currentColumnEnd: number = getTimestamp(
                currentColumn[currentColumn.length - 1].event.end
            );

            if (
                getTimestamp(currentEvent.start) >= currentColumnEnd ||
                currentColumnStart >= getTimestamp(currentEvent.end)
            ) {
                // This case there is no overlap, push the event into the column and remove it from the remaining list of items
                currentColumn.push({
                    event: currentEvent,
                    startColumn: columns.length,
                    endColumn: columns.length,
                });
                remainingEvents.splice(currentEventIndex, 1);
            } else {
                // In this case this event overlaps and hence skip this
                currentEventIndex++;
            }
        }

        // Add the new group
        columns.push(currentColumn);
    }

    for (const currentColumn of columns) {
        for (const columnEvent of currentColumn) {
            columnEvent.endColumn = correctColumnEvent(columnEvent, columns);
        }
    }

    return columnGroup;
};

/**
 * Updates event column end - the last columns the item may expand without overlapping with items in next columns
 * @param columnEvent the item
 * @param columns the columns
 * @param startColumn item's starting column
 */
const correctColumnEvent = (columnEvent: ColumnEvent, columns: ColumnEvent[][]): number => {
    const { startColumn } = columnEvent;
    if (startColumn >= columns.length) {
        return -1;
    } else if (startColumn === columns.length - 1) {
        // For an event in the last column, it can never expand
        return startColumn;
    } else {
        let candidateEnd: number = startColumn + 1;
        while (candidateEnd < columns.length) {
            const candidateColumnEvents: ColumnEvent[] = columns[candidateEnd];
            // if any events overlap in this column for that event, the previous column is the end for the input event
            for (const checkColumnEvent of candidateColumnEvents) {
                if (
                    !(
                        getTimestamp(checkColumnEvent.event.start) >=
                            getTimestamp(columnEvent.event.end) ||
                        getTimestamp(columnEvent.event.start) >=
                            getTimestamp(checkColumnEvent.event.end)
                    )
                ) {
                    return candidateEnd - 1;
                }
            }
            candidateEnd++;
        }
    }

    return columns.length - 1;
};

export default arrangeIntoDisjointColumns;
